// src/app/modules/Dashboard/_redux/dashboard/Slice.js
import { createSlice } from "@reduxjs/toolkit";

const initialDashboardState = {
  listLoading: false,
  actionsLoading: false,
  totalCount: 0,
  entities: null,
  DataForEdit: undefined,
  lastError: null,
  accounts: [],
  accountsMeta: null,
  dashboardData: null,
  accessCodes: [],
  accessCodesMeta: null,
  accessLogs: [],
  accessLogsMeta: null,
  accessCodeStats: null,

   integrations:     [],   // Integration[]
  integrationsMeta: null, // pagination meta (if ever paginated)
  integrationDetail: null,
  integrationSyncLogs: [],
  integrationSyncLogsMeta: null,


  liveStaff:              [],   // StaffLiveRecord[] from /operations/live-overview
  liveKPI:                null, // { total, on_duty, on_break, late, off_duty, fatigue_flags }
  residentLive:           [],   // ResidentLiveRecord[] from /operations/residents/live
  residentLiveSummary:    null, // { total, inside, outside }
  visitorLive:            [],   // VisitorLiveRecord[] from /operations/visitors/live
  visitorLiveSummary:     null, // { total, active, checked_out, overstay }
  mgmtAnalytics:          null, // Full analytics payload from /operations/mgmt-analytics

  members: [],
  membersMeta: null,
  memberDetails: null,
  memberStats: null,

  intelligence_hotspots: [],
  intelligence_heatmap: null,
  intelligence_time: null,
  intelligence_location_risk: null,
  

  reports: [],
  reportsMeta: null,

  incidents: [],
  incidentsMeta: null,
  incidentDetails: null,
  incidentStats: null,

  safetyScore: null,
  timeAnalysis: null,
  incidentPatterns: null,
  riskPrediction: null,
  nearbyIncidents: [],


  // Security Analytics
  security_analytics: null,
  threat_alerts: [],
  threat_alerts_meta: null,
  
  // NEW: Visitors
  visitors: [],
  visitorsMeta: null,
  visitorProfile: null,
  visitorActivity: null,
  visitorStats: null,
  highRiskVisitors: [],

  blacklist: [],
  blacklistMeta: null,

  
  spacelist: [],
  spaceMeta: null,


  subscriptionLoading: false,
  billingPlans: [],
  subscription: null,
  invoices: [],
  invoicesMeta: null,
  invoiceDetail: null,
  paymentMethods: [],
  billingLoading: false,


  networkStats: null,


  staff: [],
  staffMeta: null,
  activeStaff: [],
  staffAttendance: null,
  staffDetails: null,

  // Resident Operations
  residents: [],
  residentsMeta: null,
  residentDetails: null,
  residentHistory: null,

  // Unified Dashboard
  unifiedDashboard: null,
  operationsLogs: [],
  operationsLogsMeta: null,
  capacityStatus: null,
  operationsAnalytics: null,

  // Events / Ticketing
  events: [],
  eventsMeta: null,
  eventDetail: null,
  eventStats: null,
  eventOrders: [],
  eventOrdersMeta: null,
  allOrders: [],
  allOrdersMeta: null,

  // Wallet
  wallet: null,
  walletTransactions: [],
  walletTransactionsMeta: null,
  bankAccounts: [],
};

export const callTypes = {
  list: "list",
  action: "action"
};

export const DashboardSlice = createSlice({
  name: "dashboard",
  initialState: initialDashboardState,
  reducers: {
    catchError: (state, action) => {
      state.error = `${action.type}: ${action.payload.error}`;
      if (action.payload.callType === callTypes.list) {
        state.listLoading = false;
      } else {
        state.actionsLoading = false;
      }
    },
    startCall: (state, action) => {
      state.error = null;
      if (action.payload.callType === callTypes.list) {
        state.listLoading = true;
      } else {
        state.actionsLoading = true;
      }
    },
    stopLoading: (state) => {
      state.actionsLoading = false;
      state.listLoading = false;
    },
    
    // Dashboard data
    dashboardData: (state, action) => {
      state.dashboardData = action.payload;
      state.listLoading = false;
    },
    
    // Accounts/Users
    accounts: (state, action) => {
      state.accounts = action.payload;
      state.listLoading = false;
    },
    accountsMeta: (state, action) => {
      state.accountsMeta = action.payload;
    },

    blacklist: (state, action) => {
      state.blacklist = action.payload;
      state.listLoading = false;
    },
    blacklistMeta: (state, action) => {
      state.blacklistMeta = action.payload;
    },

    space: (state, action) => {
      state.space = action.payload;
      state.listLoading = false;
    },
    spaceMeta: (state, action) => {
      state.spaceMeta = action.payload;
    },

  updateVisitorByCodeId: (state, action) => {
    const { code_id, status, log_id } = action.payload;
    const idx = state.visitorLive.findIndex(v => v.code_id === code_id);
    if (idx !== -1) {
      state.visitorLive[idx].status = status;
      if (log_id) state.visitorLive[idx].log_id = log_id;
      if (status === 'checked_in') state.visitorLive[idx].check_in = new Date().toISOString();
    }
  },


    networkStats: (state, action) => {
      state.networkStats = action.payload;
      state.listLoading = false;
    },
    
    // Access Codes
    accessCodes: (state, action) => {
      state.accessCodes = action.payload;
      state.listLoading = false;
    },
    accessCodesMeta: (state, action) => {
      state.accessCodesMeta = action.payload;
    },
    codeAdded: (state, action) => {
      state.accessCodes = [action.payload, ...(state.accessCodes || [])];
      state.actionsLoading = false;
    },

    safetyScore: (state, action) => {
      state.safetyScore = action.payload;
      state.listLoading = false;
    },
    timeAnalysis: (state, action) => {
      state.timeAnalysis = action.payload;
      state.listLoading = false;
    },
    incidentPatterns: (state, action) => {
      state.incidentPatterns = action.payload;
      state.listLoading = false;
    },
    riskPrediction: (state, action) => {
      state.riskPrediction = action.payload;
      state.listLoading = false;
    },
    nearbyIncidents: (state, action) => {
      state.nearbyIncidents = action.payload;
      state.listLoading = false;
    },
    
    // Access Logs
    accessLogs: (state, action) => {
      state.accessLogs = action.payload;
      state.listLoading = false;
    },
    accessLogsMeta: (state, action) => {
      state.accessLogsMeta = action.payload;
    },
    
    reports: (state, action) => {
      state.reports = action.payload;
      state.listLoading = false;
    },
    reportsMeta: (state, action) => {
      state.reportsMeta = action.payload;
    },

    incidents: (state, action) => {
      state.incidents = action.payload;
      state.listLoading = false;
    },
    incidentsMeta: (state, action) => {
      state.incidentsMeta = action.payload;
    },
    incidentDetails: (state, action) => {
      state.incidentDetails = action.payload;
      state.listLoading = false;
    },
    incidentStats: (state, action) => {
      state.incidentStats = action.payload;
      state.listLoading = false;
    },

    // In reducers, add:
    intelligence_hotspots: (state, action) => {
      state.intelligence_hotspots = action.payload;
      state.listLoading = false;
    },
    intelligence_heatmap: (state, action) => {
      state.intelligence_heatmap = action.payload;
      state.listLoading = false;
    },
    intelligence_time: (state, action) => {
      state.intelligence_time = action.payload;
      state.listLoading = false;
    },
    intelligence_location_risk: (state, action) => {
      state.intelligence_location_risk = action.payload;
      state.listLoading = false;
    },


    // Access Code Stats
    accessCodeStats: (state, action) => {
      state.accessCodeStats = action.payload;
      state.listLoading = false;
    },
    
    // Security Analytics
    securityAnalytics: (state, action) => {
      state.security_analytics = action.payload;
      state.listLoading = false;
    },
    
    // Threat Alerts
    threatAlerts: (state, action) => {
      state.threat_alerts = action.payload;
      state.listLoading = false;
    },
    threatAlertsMeta: (state, action) => {
      state.threat_alerts_meta = action.payload;
    },
    
    // NEW: Visitors
    visitors: (state, action) => {
      state.visitors = action.payload;
      state.listLoading = false;
    },
    visitorsMeta: (state, action) => {
      state.visitorsMeta = action.payload;
    },
    visitorProfile: (state, action) => {
      state.visitorProfile = action.payload;
      state.listLoading = false;
    },
    visitorActivity: (state, action) => {
      state.visitorActivity = action.payload;
      state.listLoading = false;
    },
    visitorStats: (state, action) => {
      state.visitorStats = action.payload;
      state.listLoading = false;
    },
    highRiskVisitors: (state, action) => {
      state.highRiskVisitors = action.payload;
      state.listLoading = false;
    },
    visitorFlagged: (state, action) => {
      state.actionsLoading = false;
      // Update visitor in list if it exists
      const visitorIndex = state.visitors.findIndex(v => v.profile_id === action.payload.profile_id);
      if (visitorIndex !== -1) {
        state.visitors[visitorIndex] = action.payload;
      }
    },
    visitorUnflagged: (state, action) => {
      state.actionsLoading = false;
      const visitorIndex = state.visitors.findIndex(v => v.profile_id === action.payload.profile_id);
      if (visitorIndex !== -1) {
        state.visitors[visitorIndex] = action.payload;
      }
    },
    threatAlertAdded: (state, action) => {
      state.actionsLoading = false;
    },
    visitorBlacklisted: (state, action) => {
      state.actionsLoading = false;
    },
    
    // Legacy support (keep for backward compatibility)
    estateCodes: (state, action) => {
      state.accessCodes = action.payload;
      state.listLoading = false;
    },
    estateLogs: (state, action) => {
      state.accessLogs = action.payload;
      state.listLoading = false;
    },
    estateLogsMeta: (state, action) => {
      state.accessLogsMeta = action.payload;
    },
    contractorLogs: (state, action) => {
      state.contractorLogs = action.payload;
      state.listLoading = false;
    },
    contractorLogsMeta: (state, action) => {
      state.contractorLogsMeta = action.payload;
    },
    
    // Generic CRUD actions
    DataFetched: (state, action) => {
      state.actionsLoading = false;
      state.DataForEdit = action.payload.DataForEdit;
      state.error = null;
    },
    DataDeleted: (state, action) => {
      state.error = null;
      state.actionsLoading = false;
      state.entities = state.entities.filter(el => el.id !== action.payload.id);
    },
    DataUpdated: (state, action) => {
      state.error = null;
      state.actionsLoading = false;
      state.entities = state.entities.map(entity => {
        if (entity.id === action.payload.Data.id) {
          return action.payload.Data;
        }
        return entity;
      });
    },
    DatasStatusUpdated: (state, action) => {
      state.actionsLoading = false;
      state.error = null;
      const { ids, status } = action.payload;
      state.entities = state.entities.map(entity => {
        if (ids.findIndex(id => id === entity.id) > -1) {
          entity.status = status;
        }
        return entity;
      });
    },
    DatasDeleted: (state, action) => {
      state.error = null;
      state.actionsLoading = false;
      state.entities = state.entities.filter(
        el => !action.payload.ids.includes(el.id)
      );
    },

     members: (state, action) => {
      state.members = action.payload;
      state.listLoading = false;
    },
    membersMeta: (state, action) => {
      state.membersMeta = action.payload;
    },
    memberDetails: (state, action) => {
      state.memberDetails = action.payload;
      state.listLoading = false;
    },
    memberStats: (state, action) => {
      state.memberStats = action.payload;
      state.listLoading = false;
    },
    memberAdded: (state, action) => {
      state.members = [action.payload, ...(state.members || [])];
      state.actionsLoading = false;
    },
    memberUpdated: (state, action) => {
      state.actionsLoading = false;
      const memberIndex = state.members.findIndex(m => m.membership_id === action.payload.membership_id);
      if (memberIndex !== -1) {
        state.members[memberIndex] = action.payload;
      }
    },
    memberDeleted: (state, action) => {
      state.actionsLoading = false;
      state.members = state.members.filter(m => m.membership_id !== action.payload);
    },
    memberSuspended: (state, action) => {
      state.actionsLoading = false;
      const memberIndex = state.members.findIndex(m => m.membership_id === action.payload.membership_id);
      if (memberIndex !== -1) {
        state.members[memberIndex] = action.payload;
      }
    },
    memberReactivated: (state, action) => {
      state.actionsLoading = false;
      const memberIndex = state.members.findIndex(m => m.membership_id === action.payload.membership_id);
      if (memberIndex !== -1) {
        state.members[memberIndex] = action.payload;
      }
    },

     // NEW: Invite actions
    membersInvited: (state, action) => {
      state.actionsLoading = false;
      // Don't update the members list here - it will be refreshed when the modal closes
    },
    inviteResent: (state, action) => {
      state.actionsLoading = false;
      // Optionally update the member in the list
      const memberIndex = state.members.findIndex(m => m.membership_id === action.payload.membership_id);
      if (memberIndex !== -1) {
        state.members[memberIndex] = action.payload;
      }
    },
    inviteCancelled: (state, action) => {
      state.actionsLoading = false;
      // Remove the cancelled invite from the members list
      state.members = state.members.filter(m => m.membership_id !== action.payload);
    },




    // Staff Operations Reducers
    staff: (state, action) => {
      state.staff = action.payload;
      state.listLoading = false;
    },
    staffMeta: (state, action) => {
      state.staffMeta = action.payload;
    },
    activeStaff: (state, action) => {
      state.activeStaff = action.payload;
      state.listLoading = false;
    },
    staffAttendance: (state, action) => {
      state.staffAttendance = action.payload;
      state.listLoading = false;
    },
    staffDetails: (state, action) => {
      state.staffDetails = action.payload;
      state.listLoading = false;
    },

    // Resident Operations Reducers
    residents: (state, action) => {
      state.residents = action.payload;
      state.listLoading = false;
    },
    residentsMeta: (state, action) => {
      state.residentsMeta = action.payload;
    },
    residentDetails: (state, action) => {
      state.residentDetails = action.payload;
      state.listLoading = false;
    },
    residentHistory: (state, action) => {
      state.residentHistory = action.payload;
      state.listLoading = false;
    },

    // Unified Dashboard Reducers
    unifiedDashboard: (state, action) => {
      state.unifiedDashboard = action.payload;
      state.listLoading = false;
    },
    operationsLogs: (state, action) => {
      state.operationsLogs = action.payload;
      state.listLoading = false;
    },
    operationsLogsMeta: (state, action) => {
      state.operationsLogsMeta = action.payload;
    },
    capacityStatus: (state, action) => {
      state.capacityStatus = action.payload;
      state.listLoading = false;
    },
    operationsAnalytics: (state, action) => {
      state.operationsAnalytics = action.payload;
      state.listLoading = false;
    },

    subscriptionFetching: (state) => {
      state.subscriptionLoading = true;
    },

  billingPlans: (state, action) => {
    state.billingPlans = action.payload;
    state.listLoading = false;
  },
  subscription: (state, action) => {
    state.subscriptionLoading = false;
    state.subscription = action.payload;
    state.listLoading = false;
  },
  invoices: (state, action) => {
    state.invoices = action.payload;
    state.listLoading = false;
  },
  invoicesMeta: (state, action) => {
    state.invoicesMeta = action.payload;
  },
  invoiceDetail: (state, action) => {
    state.invoiceDetail = action.payload;
    state.listLoading = false;
  },
  paymentMethods: (state, action) => {
    state.paymentMethods = action.payload;
    state.listLoading = false;
  },
  paymentMethodRemoved: (state, action) => {
    state.actionsLoading = false;
    state.paymentMethods = state.paymentMethods.filter(
      pm => pm.payment_method_id !== action.payload
    );
  },
  defaultPaymentMethodSet: (state, action) => {
    state.actionsLoading = false;
    state.paymentMethods = state.paymentMethods.map(pm => ({
      ...pm,
      is_default: pm.payment_method_id === action.payload.payment_method_id,
    }));
  },
  invoicePaid: (state, action) => {
    state.actionsLoading = false;
    const idx = state.invoices.findIndex(inv => inv.invoice_id === action.payload.invoice_id);
    if (idx !== -1) state.invoices[idx] = action.payload;
  },
  subscriptionCancelled: (state, action) => {
    state.actionsLoading = false;
    state.subscription = action.payload;
  },  


  billingPlans: (state, action) => {
    state.billingPlans = action.payload;
    state.listLoading = false;
  },
  subscription: (state, action) => {
    state.subscription = action.payload;
    state.listLoading = false;
  },
  invoices: (state, action) => {
    state.invoices = action.payload;
    state.listLoading = false;
  },
  invoicesMeta: (state, action) => {
    state.invoicesMeta = action.payload;
  },
  invoiceDetail: (state, action) => {
    state.invoiceDetail = action.payload;
    state.listLoading = false;
  },
  paymentMethods: (state, action) => {
    state.paymentMethods = action.payload;
    state.listLoading = false;
  },
  paymentMethodRemoved: (state, action) => {
    state.actionsLoading = false;
    state.paymentMethods = state.paymentMethods.filter(
      pm => pm.payment_method_id !== action.payload
    );
  },
  defaultPaymentMethodSet: (state, action) => {
    state.actionsLoading = false;
    state.paymentMethods = state.paymentMethods.map(pm => ({
      ...pm,
      is_default: pm.payment_method_id === action.payload.payment_method_id,
    }));
  },
  invoicePaid: (state, action) => {
    state.actionsLoading = false;
    const idx = state.invoices.findIndex(inv => inv.invoice_id === action.payload.invoice_id);
    if (idx !== -1) state.invoices[idx] = action.payload;
  },
  subscriptionCancelled: (state, action) => {
    state.actionsLoading = false;
    state.subscription = action.payload;
  },

   setLiveStaff: (state, action) => {
    state.listLoading = false;
    state.liveStaff   = action.payload;
  },
 
  setLiveKPI: (state, action) => {
    state.liveKPI = action.payload;
  },
 
  updateLiveStaffStatus: (state, action) => {
    const { user_id, status } = action.payload;
    const idx = state.liveStaff.findIndex(s => s.user_id === user_id || s.id === user_id);
    if (idx !== -1) {
      state.liveStaff[idx].status = status;
      if (status === 'off_duty') state.liveStaff[idx].entry_time = null;
    }
  },
 
  updateLiveStaffRole: (state, action) => {
    const { membership_id, role } = action.payload;
    const idx = state.liveStaff.findIndex(s => s.membership_id === membership_id);
    if (idx !== -1) state.liveStaff[idx].role = role;
  },
 
  setResidentLive: (state, action) => {
    state.listLoading    = false;
    state.residentLive   = action.payload;
  },
 
  setResidentLiveSummary: (state, action) => {
    state.residentLiveSummary = action.payload;
  },
 
  setVisitorLive: (state, action) => {
    state.listLoading  = false;
    state.visitorLive  = action.payload;
  },
 
  setVisitorLiveSummary: (state, action) => {
    state.visitorLiveSummary = action.payload;
  },
 
  updateVisitorLiveStatus: (state, action) => {
    const { log_id, status } = action.payload;
    const idx = state.visitorLive.findIndex(v => v.log_id === log_id || v.id === log_id);
    if (idx !== -1) {
      state.visitorLive[idx].status = status;
      if (status === 'checked_out') state.visitorLive[idx].check_out = new Date().toISOString();
    }
  },
 
  setMgmtAnalytics: (state, action) => {
    state.listLoading    = false;
    state.mgmtAnalytics  = action.payload;
  },


  integrations: (state, action) => {
    state.integrations = action.payload;
    state.listLoading  = false;
  },
  integrationDetail: (state, action) => {
    state.integrationDetail = action.payload;
    state.listLoading       = false;
  },
  integrationAdded: (state, action) => {
    state.actionsLoading = false;
    state.integrations   = [action.payload, ...(state.integrations || [])];
  },
  integrationUpdated: (state, action) => {
    state.actionsLoading = false;
    const idx = state.integrations.findIndex(
      i => i.integration_id === action.payload.integration_id
    );
    if (idx !== -1) state.integrations[idx] = action.payload;
  },
  integrationDeleted: (state, action) => {
    state.actionsLoading = false;
    state.integrations   = state.integrations.filter(
      i => i.integration_id !== action.payload
    );
  },
  integrationSyncLogs: (state, action) => {
    state.integrationSyncLogs     = action.payload.logs  || [];
    state.integrationSyncLogsMeta = action.payload.meta  || null;
    state.listLoading             = false;
  },

  // ── Events ────────────────────────────────────────────────────────────────
  events: (state, action) => {
    state.events = action.payload;
    state.listLoading = false;
  },
  eventsMeta: (state, action) => {
    state.eventsMeta = action.payload;
  },
  eventDetail: (state, action) => {
    state.eventDetail = action.payload;
    state.listLoading = false;
  },
  eventStats: (state, action) => {
    state.eventStats = action.payload;
    state.listLoading = false;
  },
  eventOrders: (state, action) => {
    state.eventOrders = action.payload;
    state.listLoading = false;
  },
  eventOrdersMeta: (state, action) => {
    state.eventOrdersMeta = action.payload;
  },
  allOrders: (state, action) => {
    state.allOrders = action.payload;
    state.listLoading = false;
  },
  allOrdersMeta: (state, action) => {
    state.allOrdersMeta = action.payload;
  },
  eventAdded: (state, action) => {
    state.events = [action.payload, ...(state.events || [])];
    state.actionsLoading = false;
  },
  eventUpdated: (state, action) => {
    state.actionsLoading = false;
    const idx = state.events.findIndex(e => e.event_id === action.payload.event_id);
    if (idx !== -1) state.events[idx] = action.payload;
    if (state.eventDetail?.event_id === action.payload.event_id) state.eventDetail = action.payload;
  },
  eventDeleted: (state, action) => {
    state.actionsLoading = false;
    state.events = state.events.filter(e => e.event_id !== action.payload);
  },

  // ── Wallet ────────────────────────────────────────────────────────────────
  wallet: (state, action) => {
    state.wallet = action.payload;
    state.listLoading = false;
  },
  walletTransactions: (state, action) => {
    state.walletTransactions = action.payload;
    state.listLoading = false;
  },
  walletTransactionsMeta: (state, action) => {
    state.walletTransactionsMeta = action.payload;
  },
  bankAccounts: (state, action) => {
    state.bankAccounts = action.payload;
    state.listLoading = false;
  },
  bankAccountAdded: (state, action) => {
    state.actionsLoading = false;
    state.bankAccounts = [...(state.bankAccounts || []), action.payload];
  },
  bankAccountRemoved: (state, action) => {
    state.actionsLoading = false;
    state.bankAccounts = state.bankAccounts.filter(b => b.bank_account_id !== action.payload);
  },
  defaultBankAccountSet: (state, action) => {
    state.actionsLoading = false;
    state.bankAccounts = state.bankAccounts.map(b => ({
      ...b,
      is_default: b.bank_account_id === action.payload,
    }));
  },

  }
});